import React, { useMemo, useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Row,
} from "../../../components/Component";
import {
  Alert,
  Badge,
  Card,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Button as RButton,
} from "reactstrap";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { ISO_3166_ALPHA2_OPTIONS } from "../../../utils/iso3166Alpha2Options";
import { openPreview, previewPayload } from "./prescriptionPreview";

const PATIENT_FIELD_OPTIONS = [
  { value: "displayName", label: "Patient name" },
  { value: "sex", label: "Sex" },
  { value: "dateOfBirth", label: "Date of birth" },
  { value: "weight", label: "Weight" },
  { value: "height", label: "Height" },
  { value: "phone", label: "Phone" },
  { value: "address", label: "Address" },
  { value: "allergies", label: "Allergies" },
  { value: "chronicConditions", label: "Chronic conditions" },
];

const ITEM_FIELD_OPTIONS = [
  { value: "name", label: "Name (DCI/denomination)" },
  { value: "posology", label: "Posology" },
  { value: "durationOrRenewals", label: "Duration or renewals" },
  { value: "dosage", label: "Dosage" },
  { value: "form", label: "Form" },
  { value: "route", label: "Route" },
  { value: "renewals", label: "Renewals" },
];

const PRACTITIONER_FIELD_OPTIONS = [
  { value: "displayName", label: "Practitioner name" },
  { value: "specialties", label: "Specialties" },
  { value: "professionalId", label: "Professional ID / registration" },
  { value: "diplomas", label: "Diplomas" },
  { value: "emergencyContact", label: "Emergency contact" },
  { value: "insuranceStatus", label: "Insurance status" },
];

const EMPTY_FORM = {
  countryCode: "",
  countryName: "",
  patientRequired: ["displayName", "sex", "dateOfBirth"],
  itemRequired: ["name", "posology", "durationOrRenewals"],
  practitionerRequired: [],
  signatureRequired: true,
  notes: "",
  legalHeader: "",
  crossBorderDisclaimer: "",
  licenseTypes: [],
  languages: [],
  mandatoryMentions: [],
};

const COUNTRY_CODE_SUFFIX_RE = /\s\([A-Z]{2}\)$/;

const parseCountryName = (label, code) => {
  const text = String(label || "").trim();
  if (!text) return code;
  return text.replace(COUNTRY_CODE_SUFFIX_RE, "").trim() || code;
};

const licenseTypesSummary = (reg) => {
  const types =
    Array.isArray(reg.licenseTypes) && reg.licenseTypes.length
      ? reg.licenseTypes
      : reg.licenseNumberLabel
        ? [{ code: reg.licenseNumberLabel, label: reg.licenseNumberLabel }]
        : [];
  if (!types.length) return "—";
  return types.map((row) => row.code || row.label).filter(Boolean).join(", ");
};

const languagesSummary = (reg) => {
  const langs =
    Array.isArray(reg.languages) && reg.languages.length
      ? reg.languages
      : reg.documentLanguage
        ? [reg.documentLanguage]
        : [];
  return langs.length ? langs.join(", ") : "—";
};

const mentionCount = (reg) =>
  Array.isArray(reg.mandatoryMentions) ? reg.mandatoryMentions.filter((line) => String(line || "").trim()).length : 0;

const regulationStatus = (reg) => {
  const hasLicenses = licenseTypesSummary(reg) !== "—";
  const hasLanguages = languagesSummary(reg) !== "—";
  if (hasLicenses && hasLanguages) {
    return { label: "Configured", color: "success" };
  }
  return { label: "Incomplete", color: "warning" };
};

/**
 * @param {Record<string, unknown> | null | undefined} reg
 * @returns {typeof EMPTY_FORM}
 */
function formFromRegulation(reg) {
  if (!reg) return { ...EMPTY_FORM };
  const licenseTypes = Array.isArray(reg.licenseTypes) && reg.licenseTypes.length
    ? reg.licenseTypes.map((row) => ({
        code: row.code || "",
        label: row.label || row.code || "",
        required: row.required === true,
      }))
    : reg.licenseNumberLabel
      ? [{ code: String(reg.licenseNumberLabel).trim(), label: String(reg.licenseNumberLabel).trim(), required: true }]
      : [];
  const languages = Array.isArray(reg.languages) && reg.languages.length
    ? [...reg.languages]
    : reg.documentLanguage
      ? [String(reg.documentLanguage).trim()]
      : [];
  return {
    countryCode: reg.countryCode || "",
    countryName: reg.countryName || "",
    patientRequired: Array.isArray(reg.patientRequired) ? [...reg.patientRequired] : [],
    itemRequired: Array.isArray(reg.itemRequired) ? [...reg.itemRequired] : [],
    practitionerRequired: Array.isArray(reg.practitionerRequired) ? [...reg.practitionerRequired] : [],
    signatureRequired: reg.signatureRequired !== false,
    notes: reg.notes || "",
    legalHeader: reg.legalHeader || "",
    crossBorderDisclaimer: reg.crossBorderDisclaimer || "",
    licenseTypes,
    languages,
    mandatoryMentions: Array.isArray(reg.mandatoryMentions) ? [...reg.mandatoryMentions] : [],
  };
}

const PrescriptionRegulationsPage = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [formPreviewError, setFormPreviewError] = useState(null);
  const configuredCodes = useMemo(
    () => new Set(list.map((row) => String(row.countryCode || "").trim().toUpperCase()).filter(Boolean)),
    [list]
  );
  const countryOptions = useMemo(
    () => ISO_3166_ALPHA2_OPTIONS.filter((opt) => !configuredCodes.has(opt.value)),
    [configuredCodes]
  );
  const isCreate = !editingCode;
  const selectedCode = String(form.countryCode || "").trim().toUpperCase();
  const duplicateCountry = isCreate && selectedCode && configuredCodes.has(selectedCode);

  const fetchList = (options = {}) => {
    const { signal } = options;
    setLoading(true);
    setListError(null);
    http
      .get("/api/v1/prescription-regulations", { signal })
      .then((res) => {
        setList(res.data?.data || []);
        setListError(null);
      })
      .catch((err) => {
        if (!isAbortError(err)) {
          setList([]);
          setListError(
            err.response?.data?.message || err.response?.data?.error || "Unable to load prescription regulations."
          );
        }
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  };

  useEffect(() => {
    const ac = new AbortController();
    fetchList({ signal: ac.signal });
    return () => ac.abort();
  }, []);

  const openAdd = () => {
    setEditingCode(null);
    setForm({ ...EMPTY_FORM });
    setError(null);
    setFormPreviewError(null);
    setModalOpen(true);
  };

  const openEdit = (reg) => {
    setEditingCode(reg.countryCode);
    setForm(formFromRegulation(reg));
    setError(null);
    setFormPreviewError(null);
    setModalOpen(true);
  };

  const toggleArray = (field, value) => {
    setForm((prev) => {
      const arr = prev[field] || [];
      const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
      return { ...prev, [field]: next };
    });
  };

  const addLicenseType = () => {
    setForm((prev) => ({
      ...prev,
      licenseTypes: [...(prev.licenseTypes || []), { code: "", label: "", required: false }],
    }));
  };

  const updateLicenseType = (index, field, value) => {
    setForm((prev) => {
      const rows = [...(prev.licenseTypes || [])];
      const row = { ...rows[index], [field]: field === "code" ? String(value).toUpperCase() : value };
      rows[index] = row;
      return { ...prev, licenseTypes: rows };
    });
  };

  const removeLicenseType = (index) => {
    setForm((prev) => ({
      ...prev,
      licenseTypes: (prev.licenseTypes || []).filter((_, i) => i !== index),
    }));
  };

  const addLanguage = () => {
    setForm((prev) => ({ ...prev, languages: [...(prev.languages || []), ""] }));
  };

  const updateLanguage = (index, value) => {
    setForm((prev) => {
      const rows = [...(prev.languages || [])];
      rows[index] = String(value).trim().toLowerCase();
      return { ...prev, languages: rows };
    });
  };

  const removeLanguage = (index) => {
    setForm((prev) => ({
      ...prev,
      languages: (prev.languages || []).filter((_, i) => i !== index),
    }));
  };

  const addMention = () => {
    setForm((prev) => ({
      ...prev,
      mandatoryMentions: [...(prev.mandatoryMentions || []), ""],
    }));
  };

  const updateMention = (index, value) => {
    setForm((prev) => {
      const rows = [...(prev.mandatoryMentions || [])];
      rows[index] = value;
      return { ...prev, mandatoryMentions: rows };
    });
  };

  const removeMention = (index) => {
    setForm((prev) => ({
      ...prev,
      mandatoryMentions: (prev.mandatoryMentions || []).filter((_, i) => i !== index),
    }));
  };

  const moveMention = (index, dir) => {
    setForm((prev) => {
      const rows = [...(prev.mandatoryMentions || [])];
      const target = index + dir;
      if (target < 0 || target >= rows.length) return prev;
      const tmp = rows[index];
      rows[index] = rows[target];
      rows[target] = tmp;
      return { ...prev, mandatoryMentions: rows };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const code = (form.countryCode || "").trim().toUpperCase().slice(0, 2);
    if (!code || code.length !== 2) {
      setError("Country code must be 2 letters (e.g. FR, GB, IN, CA).");
      return;
    }
    if (!form.countryName || !form.countryName.trim()) {
      setError("Country name is required.");
      return;
    }
    if (!editingCode && configuredCodes.has(code)) {
      setError("A regulation already exists for this country. Please select another country.");
      return;
    }
    setSubmitLoading(true);
    const licenseTypes = (form.licenseTypes || [])
      .map((row) => ({
        code: String(row.code || "").trim().toUpperCase(),
        label: String(row.label || row.code || "").trim(),
        required: row.required === true,
      }))
      .filter((row) => row.code && row.label);
    const languages = (form.languages || [])
      .map((code) => String(code || "").trim().toLowerCase())
      .filter(Boolean);
    const mandatoryMentions = (form.mandatoryMentions || [])
      .map((line) => String(line || "").trim())
      .filter(Boolean);

    const payload = {
      countryCode: code,
      countryName: form.countryName.trim(),
      patientRequired: form.patientRequired,
      itemRequired: form.itemRequired,
      practitionerRequired: form.practitionerRequired,
      signatureRequired: form.signatureRequired,
      notes: form.notes.trim(),
      legalHeader: (form.legalHeader || "").trim(),
      crossBorderDisclaimer: (form.crossBorderDisclaimer || "").trim(),
      licenseTypes,
      languages,
      mandatoryMentions,
    };
    const promise = editingCode
      ? http.put(`/api/v1/prescription-regulations/${editingCode}`, payload)
      : http.post("/api/v1/prescription-regulations", payload);
    promise
      .then(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setModalOpen(false);
        fetchList();
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Request failed.");
      })
      .finally(() => setSubmitLoading(false));
  };

  const requestDelete = (countryCode) => {
    setDeleteError(null);
    setDeleteTarget(countryCode);
  };

  const closeDeleteModal = () => {
    if (!deleteLoading) {
      setDeleteTarget(null);
      setDeleteError(null);
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteError(null);
    http
      .delete(`/api/v1/prescription-regulations/${deleteTarget}`)
      .then(() => {
        setDeleteTarget(null);
        fetchList();
      })
      .catch((err) => setDeleteError(err.response?.data?.message || "Delete failed."))
      .finally(() => setDeleteLoading(false));
  };

  const runPreview = async (key, body, setLocalError) => {
    setPreviewLoadingKey(key);
    setLocalError(null);
    try {
      await openPreview(http, body);
    } catch (err) {
      setLocalError(
        err?.message && String(err.message).trim()
          ? String(err.message).trim()
          : "Unable to open the PDF preview. Please try again."
      );
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const previewListRow = (reg) => {
    const code = String(reg.countryCode || "").trim().toUpperCase();
    if (!code) return;
    runPreview(`list-${code}`, { countryCode: code }, setPreviewError);
  };

  const previewCurrentForm = () => {
    const code = String(form.countryCode || "").trim().toUpperCase();
    if (!code || code.length !== 2) {
      setFormPreviewError("Select a country before previewing the PDF.");
      return;
    }
    if (!form.countryName || !String(form.countryName).trim()) {
      setFormPreviewError("Enter a country name before previewing the PDF.");
      return;
    }
    runPreview("form", previewPayload(form), setFormPreviewError);
  };

  return (
    <>
      <Head title="Prescription regulations" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Prescription regulations by country
              </BlockTitle>
              <BlockDes className="text-soft">
                Configure mandatory fields for prescriptions per country for video consultations. The
                applicable regulation follows the clinic&apos;s country (for example, a UK patient with a
                clinician in India uses India rules). Use Preview PDF to review the sample layout before
                saving.
              </BlockDes>
            </BlockHeadContent>
            <Button color="primary" onClick={openAdd}>
              Add country
            </Button>
          </BlockBetween>
        </BlockHead>

        <Alert color="light" className="border">
          These rules define which fields are required on prescriptions for each country. The applicable regulation follows the
          clinic&apos;s jurisdiction when generating or validating a prescription.
        </Alert>

        <Block>
          {previewError ? (
            <Alert color="danger" className="mb-3">
              {previewError}
            </Alert>
          ) : null}
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : listError ? (
            <Alert color="danger" className="mb-0">
              {listError}
            </Alert>
          ) : list.length === 0 ? (
            <Alert color="light" className="border mb-0">
              No regulations configured. Use &quot;Add country&quot; to create the first one.
            </Alert>
          ) : (
            <Card className="card-bordered">
              <div className="table-responsive">
                <table className="table table-tranx mb-0">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>License types</th>
                      <th>Languages</th>
                      <th>Mentions</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((reg) => {
                      const status = regulationStatus(reg);
                      return (
                        <tr key={reg.countryCode}>
                          <td>
                            <Badge color="light" className="me-1">
                              {reg.countryCode}
                            </Badge>
                            {reg.countryName}
                          </td>
                          <td>{licenseTypesSummary(reg)}</td>
                          <td>{languagesSummary(reg)}</td>
                          <td>{mentionCount(reg)}</td>
                          <td>
                            <Badge color={status.color}>{status.label}</Badge>
                            {reg.signatureRequired !== false ? (
                              <span className="text-soft small d-block mt-1">Signature required</span>
                            ) : null}
                          </td>
                          <td className="text-end text-nowrap">
                            <Button
                              color="light"
                              size="sm"
                              className="me-1"
                              disabled={previewLoadingKey === `list-${reg.countryCode}`}
                              onClick={() => previewListRow(reg)}
                            >
                              <i className="ri-file-pdf-line me-1" aria-hidden />
                              {previewLoadingKey === `list-${reg.countryCode}`
                                ? "Preparing preview..."
                                : "Preview PDF"}
                            </Button>
                            <Button color="secondary" size="sm" className="me-1" onClick={() => openEdit(reg)}>
                              Edit
                            </Button>
                            <Button color="danger" size="sm" outline onClick={() => requestDelete(reg.countryCode)}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </Block>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" className="modal-dialog-scrollable">
          <ModalHeader toggle={() => setModalOpen(false)}>
            {editingCode ? `Edit ${editingCode}` : "Add prescription regulation"}
          </ModalHeader>
          <Form onSubmit={handleSubmit}>
            <ModalBody style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              {formPreviewError ? (
                <div className="alert alert-danger" role="alert">
                  {formPreviewError}
                </div>
              ) : null}
              <div className="alert alert-info small">
                Rules apply to the clinic&apos;s jurisdiction (not the patient&apos;s home country). Example: UK patient with a
                clinician in India → use India requirements when the prescribing clinic is in India. Preview uses sample
                patient and practitioner data only.
              </div>
              <h6 className="overline-title text-soft mb-3">Country</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label htmlFor="rx-reg-country-code">
                      Country <span className="text-danger">*</span>
                    </Label>
                    {editingCode ? (
                      <Input
                        id="rx-reg-country-code"
                        type="text"
                        maxLength={2}
                        placeholder="FR, GB, IN, CA"
                        value={form.countryCode}
                        onChange={(e) => setForm((p) => ({ ...p, countryCode: e.target.value.toUpperCase() }))}
                        disabled
                        autoComplete="country"
                        aria-required="true"
                      />
                    ) : (
                      <Input
                        id="rx-reg-country-code"
                        type="select"
                        value={form.countryCode}
                        onChange={(e) => {
                          const code = String(e.target.value || "").toUpperCase();
                          const option = ISO_3166_ALPHA2_OPTIONS.find((row) => row.value === code);
                          setForm((p) => ({
                            ...p,
                            countryCode: code,
                            countryName: option ? parseCountryName(option.label, option.value) : "",
                          }));
                          setError(null);
                        }}
                        aria-required="true"
                      >
                        <option value="">Select a country</option>
                        {countryOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    )}
                    {!editingCode && duplicateCountry ? (
                      <div className="text-danger small mt-1">
                        A regulation already exists for this country. Please select another country.
                      </div>
                    ) : null}
                    {!editingCode && countryOptions.length === 0 ? (
                      <div className="text-soft small mt-1">
                        All available countries are already configured.
                      </div>
                    ) : null}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label htmlFor="rx-reg-country-name">
                      Country name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="rx-reg-country-name"
                      type="text"
                      placeholder="France, United Kingdom"
                      value={form.countryName}
                      onChange={(e) => setForm((p) => ({ ...p, countryName: e.target.value }))}
                      autoComplete="country-name"
                      aria-required="true"
                    />
                  </FormGroup>
                </Col>
              </Row>
              <h6 className="overline-title text-soft mb-3 mt-2">
                Patient <Badge color="primary" pill className="text-uppercase" style={{ fontSize: "0.65rem" }}>Required</Badge>
              </h6>
              <FormGroup>
                <Label>Patient — fields marked required for this country</Label>
                <div className="d-flex flex-column flex-g-2">
                  {PATIENT_FIELD_OPTIONS.map((opt) => (
                    <label key={opt.value} className="form-check mb-0">
                      <Input
                        type="checkbox"
                        checked={(form.patientRequired || []).includes(opt.value)}
                        onChange={() => toggleArray("patientRequired", opt.value)}
                      />
                      <span className="form-check-label">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </FormGroup>
              <h6 className="overline-title text-soft mb-3 mt-2">
                Prescription line (item){" "}
                <Badge color="primary" pill className="text-uppercase" style={{ fontSize: "0.65rem" }}>Required</Badge>
              </h6>
              <FormGroup>
                <Label>Item (medication line) — required fields</Label>
                <div className="d-flex flex-column flex-g-2">
                  {ITEM_FIELD_OPTIONS.map((opt) => (
                    <label key={opt.value} className="form-check mb-0">
                      <Input
                        type="checkbox"
                        checked={(form.itemRequired || []).includes(opt.value)}
                        onChange={() => toggleArray("itemRequired", opt.value)}
                      />
                      <span className="form-check-label">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </FormGroup>
              <h6 className="overline-title text-soft mb-3 mt-2">Practitioner (optional)</h6>
              <FormGroup>
                <Label>Practitioner — required or displayed fields</Label>
                <div className="d-flex flex-column flex-g-2">
                  {PRACTITIONER_FIELD_OPTIONS.map((opt) => (
                    <label key={opt.value} className="form-check mb-0">
                      <Input
                        type="checkbox"
                        checked={(form.practitionerRequired || []).includes(opt.value)}
                        onChange={() => toggleArray("practitionerRequired", opt.value)}
                      />
                      <span className="form-check-label">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </FormGroup>
              <h6 className="overline-title text-soft mb-3 mt-2">Signature & notes</h6>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={form.signatureRequired}
                    onChange={(e) => setForm((p) => ({ ...p, signatureRequired: e.target.checked }))}
                  />
                  Signature required
                </Label>
              </FormGroup>
              <FormGroup>
                <Label>Notes (regulation reference)</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. CSP Art. R. 4127-79"
                />
              </FormGroup>

              <h6 className="overline-title text-soft mb-3 mt-4">PDF document</h6>
              <FormGroup>
                <Label>Legal header</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={form.legalHeader}
                  onChange={(e) => setForm((p) => ({ ...p, legalHeader: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Cross-border disclaimer</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={form.crossBorderDisclaimer}
                  onChange={(e) => setForm((p) => ({ ...p, crossBorderDisclaimer: e.target.value }))}
                />
              </FormGroup>

              <h6 className="overline-title text-soft mb-3 mt-2">License types</h6>
              <p className="small text-soft">Define licence identifiers for this jurisdiction. Mark types as required to block prescription issue when missing.</p>
              {(form.licenseTypes || []).map((row, index) => (
                <Row key={`lic-${index}`} className="g-2 align-items-end mb-2">
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Label className="small">Code</Label>
                      <Input
                        value={row.code}
                        maxLength={32}
                        onChange={(e) => updateLicenseType(index, "code", e.target.value)}
                        placeholder="RPPS"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="5">
                    <FormGroup className="mb-0">
                      <Label className="small">Label</Label>
                      <Input
                        value={row.label}
                        onChange={(e) => updateLicenseType(index, "label", e.target.value)}
                        placeholder="RPPS"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup check className="mb-0 pt-4">
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={row.required === true}
                          onChange={(e) => updateLicenseType(index, "required", e.target.checked)}
                        />
                        Required
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md="2" className="text-end">
                    <Button type="button" color="light" size="sm" onClick={() => removeLicenseType(index)}>
                      <i className="ri-delete-bin-line" aria-hidden />
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button type="button" color="light" size="sm" className="mb-3" onClick={addLicenseType}>
                <i className="ri-add-line me-1" aria-hidden />
                Add license type
              </Button>

              <h6 className="overline-title text-soft mb-3 mt-2">Document languages</h6>
              <p className="small text-soft">ISO 639-1 codes. The first language is the PDF default.</p>
              {(form.languages || []).map((code, index) => (
                <Row key={`lang-${index}`} className="g-2 align-items-end mb-2">
                  <Col md="10">
                    <FormGroup className="mb-0">
                      <Label className="small">{index === 0 ? "Default language" : `Language ${index + 1}`}</Label>
                      <Input
                        value={code}
                        maxLength={8}
                        onChange={(e) => updateLanguage(index, e.target.value)}
                        placeholder="fr"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="2" className="text-end">
                    <Button type="button" color="light" size="sm" onClick={() => removeLanguage(index)}>
                      <i className="ri-delete-bin-line" aria-hidden />
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button type="button" color="light" size="sm" className="mb-3" onClick={addLanguage}>
                <i className="ri-add-line me-1" aria-hidden />
                Add language
              </Button>

              <h6 className="overline-title text-soft mb-3 mt-2">Mandatory mentions</h6>
              <p className="small text-soft">Ordered footer lines on the prescription PDF.</p>
              {(form.mandatoryMentions || []).map((line, index) => (
                <Row key={`mention-${index}`} className="g-2 align-items-end mb-2">
                  <Col md="8">
                    <FormGroup className="mb-0">
                      <Label className="small">Mention {index + 1}</Label>
                      <Input
                        type="textarea"
                        rows={2}
                        value={line}
                        onChange={(e) => updateMention(index, e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4" className="text-end">
                    <Button
                      type="button"
                      color="light"
                      size="sm"
                      className="me-1"
                      disabled={index === 0}
                      onClick={() => moveMention(index, -1)}
                      aria-label="Move mention up"
                    >
                      <i className="ri-arrow-up-line" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      color="light"
                      size="sm"
                      className="me-1"
                      disabled={index === (form.mandatoryMentions || []).length - 1}
                      onClick={() => moveMention(index, 1)}
                      aria-label="Move mention down"
                    >
                      <i className="ri-arrow-down-line" aria-hidden />
                    </Button>
                    <Button type="button" color="light" size="sm" onClick={() => removeMention(index)}>
                      <i className="ri-delete-bin-line" aria-hidden />
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button type="button" color="light" size="sm" className="mb-3" onClick={addMention}>
                <i className="ri-add-line me-1" aria-hidden />
                Add mention
              </Button>

            </ModalBody>
            <ModalFooter
              className="border-top bg-white"
              style={{ position: "sticky", bottom: 0, zIndex: 2 }}
            >
              <Button
                type="button"
                color="light"
                className="me-auto"
                disabled={previewLoadingKey === "form" || submitLoading}
                onClick={previewCurrentForm}
              >
                <i className="ri-file-pdf-line me-1" aria-hidden />
                {previewLoadingKey === "form" ? "Preparing preview..." : "Preview PDF"}
              </Button>
              <Button type="button" color="light" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={submitLoading || (!editingCode && (duplicateCountry || countryOptions.length === 0))}
              >
                {submitLoading ? "Saving..." : editingCode ? "Update" : "Create"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <Modal isOpen={!!deleteTarget} toggle={closeDeleteModal} centered>
          <ModalHeader toggle={closeDeleteModal}>Delete regulation</ModalHeader>
          <ModalBody>
            {deleteError && (
              <div className="alert alert-danger" role="alert">
                {deleteError}
              </div>
            )}
            <p className="mb-0">
              Remove prescription rules for <strong>{deleteTarget}</strong>? This cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <RButton color="light" onClick={closeDeleteModal} disabled={deleteLoading}>
              Cancel
            </RButton>
            <RButton color="danger" onClick={confirmDelete} disabled={deleteLoading}>
              {deleteLoading ? "Deleting…" : "Delete"}
            </RButton>
          </ModalFooter>
        </Modal>
      </Content>
    </>
  );
};

export default PrescriptionRegulationsPage;
